// src/shared/components/Modals/ItemsSelectionModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import { X, Package, Box as BoxIcon, Truck, Plus, Minus, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../context/ThemeContext';
import { PackagingOrderDialog } from './PackagingOrderDialog';

export const ItemsSelectionModal = ({ 
  open, 
  onClose, 
  title,
  currentItems = [],
  onSave,
  maxWidth = 'sm',
  showPackagingUpsell = false, // Enable/disable packaging upsell
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  
  const [packagingDialogOpen, setPackagingDialogOpen] = useState(false);
  
  const PACKAGE_SIZES = {
    small: {
      id: 'small',
      name: t('delivery.small'),
      icon: Package,
      weight: { min: 0, max: 20 },
      dimensions: { length: 150, width: 150, height: 150 },
      defaultWeight: 5,
      defaultDimensions: { length: 30, width: 20, height: 15 },
      example: t('delivery.sizeDescriptions.small.example'),
      description: t('delivery.sizeDescriptions.small.description'),
    },
    medium: {
      id: 'medium',
      name: t('delivery.medium'),
      icon: Package,
      weight: { min: 20, max: 40 },
      dimensions: { length: 250, width: 250, height: 250 },
      defaultWeight: 25,
      defaultDimensions: { length: 50, width: 40, height: 30 },
      example: t('delivery.sizeDescriptions.medium.example'),
      description: t('delivery.sizeDescriptions.medium.description'),
    },
    large: {
      id: 'large',
      name: t('delivery.large'),
      icon: BoxIcon,
      weight: { min: 40, max: 100 },
      dimensions: { length: 350, width: 350, height: 350 },
      defaultWeight: 60,
      defaultDimensions: { length: 80, width: 60, height: 50 },
      example: t('delivery.sizeDescriptions.large.example'),
      description: t('delivery.sizeDescriptions.large.description'),
    },
    extraLarge: {
      id: 'extraLarge',
      name: t('delivery.extraLarge'),
      icon: Truck,
      weight: { min: 100, max: 500 },
      dimensions: { length: 500, width: 500, height: 500 },
      defaultWeight: 150,
      defaultDimensions: { length: 120, width: 100, height: 80 },
      example: t('delivery.sizeDescriptions.extraLarge.example'),
      description: t('delivery.sizeDescriptions.extraLarge.description'),
    },
  };

  // State for selected size category
  const [selectedSize, setSelectedSize] = useState('small');
  
  // State for items array
  const [items, setItems] = useState(() => {
    if (currentItems.length > 0) {
      return currentItems;
    }
    // Default: one small item
    return [{
      id: Date.now(),
      weight: PACKAGE_SIZES.small.defaultWeight,
      dimensions: { ...PACKAGE_SIZES.small.defaultDimensions },
      sizeCategory: 'small',
    }];
  });

  // Update selected size based on first item
  useEffect(() => {
    if (items.length > 0 && items[0].sizeCategory) {
      setSelectedSize(items[0].sizeCategory);
    }
  }, [items]);

  const handleSizeSelect = (sizeId) => {
    setSelectedSize(sizeId);
    const sizeConfig = PACKAGE_SIZES[sizeId];
    
    // Update all items to match the selected size
    setItems([{
      id: Date.now(),
      weight: sizeConfig.defaultWeight,
      dimensions: { ...sizeConfig.defaultDimensions },
      sizeCategory: sizeId,
    }]);
  };

  const handleAddItem = () => {
    const sizeConfig = PACKAGE_SIZES[selectedSize];
    setItems([...items, {
      id: Date.now(),
      weight: sizeConfig.defaultWeight,
      dimensions: { ...sizeConfig.defaultDimensions },
      sizeCategory: selectedSize,
    }]);
  };

  const handleRemoveItem = (itemId) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== itemId));
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        if (field === 'weight') {
          return { ...item, weight: Number(value) || 0 };
        } else {
          // field is a dimension (length, width, height)
          return {
            ...item,
            dimensions: {
              ...item.dimensions,
              [field]: Number(value) || 0,
            },
          };
        }
      }
      return item;
    }));
  };

  const handleSave = () => {
    onSave(items);
    onClose();
  };

  const handleReset = () => {
    const sizeConfig = PACKAGE_SIZES[selectedSize];
    setItems([{
      id: Date.now(),
      weight: sizeConfig.defaultWeight,
      dimensions: { ...sizeConfig.defaultDimensions },
      sizeCategory: selectedSize,
    }]);
    onClose();
  };

  const currentSizeConfig = PACKAGE_SIZES[selectedSize];

  // Calculate suggested packaging based on items
  const getSuggestedPackaging = () => {
    const smallCount = items.filter(item => item.sizeCategory === 'small').length;
    const otherCount = items.length - smallCount;
    
    return {
      cardboard: smallCount,
      plastic: otherCount
    };
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth={maxWidth} 
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pr: 1,
          }}>
            <Typography variant="h6" fontWeight={600}>
              {title || t('delivery.selectPackageSize')}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Size Category Selection */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {t('delivery.packageCategory') || 'Package Category'}
          </Typography>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: 1,
            mb: 3,
          }}>
            {Object.values(PACKAGE_SIZES).map((size) => {
              const Icon = size.icon;
              const isSelected = selectedSize === size.id;
              
              return (
                <Card
                  key={size.id}
                  onClick={() => handleSizeSelect(size.id)}
                  sx={{
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected 
                      ? (isDark ? 'rgba(72, 202, 228, 0.15)' : 'primary.50')
                      : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: isDark ? 'rgba(72, 202, 228, 0.15)' : 'primary.50',
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 0.5,
                    }}>
                      <Icon size={24} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {t(`delivery.${size.id}`) || size.name}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {/* Size Specifications */}
          <Card variant="outlined" sx={{ mb: 3, bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.50' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
               {t('delivery.specifications') || 'Specifications'}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('delivery.weight') || 'Weight'}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {currentSizeConfig.weight.min}-{currentSizeConfig.weight.max} kg
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('delivery.maxDimensions') || 'Max dimensions'}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {currentSizeConfig.dimensions.length} × {currentSizeConfig.dimensions.width} × {currentSizeConfig.dimensions.height} cm
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 0.5 }} />
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    📦 {currentSizeConfig.example}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {currentSizeConfig.description}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Items List */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('delivery.items') || 'Items'} ({items.length})
            </Typography>
            <Button
              size="small"
              startIcon={<Plus size={16} />}
              onClick={handleAddItem}
              sx={{ textTransform: 'none' }}
            >
              {t('delivery.addItem') || 'Add Item'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {items.map((item, index) => (
              <Card 
                key={item.id} 
                variant="outlined"
                sx={{
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: isDark ? 3 : 2,
                    borderColor: isDark ? 'rgba(72, 202, 228, 0.5)' : 'primary.light',
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('delivery.item') || 'Item'} {index + 1}
                    </Typography>
                    {items.length > 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveItem(item.id)}
                        sx={{ color: 'error.main' }}
                      >
                        <Minus size={16} />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      size="small"
                      label={t('delivery.weight') || 'Weight (kg)'}
                      type="number"
                      value={item.weight}
                      onChange={(e) => handleItemChange(item.id, 'weight', e.target.value)}
                      inputProps={{
                        min: currentSizeConfig.weight.min,
                        max: currentSizeConfig.weight.max,
                        step: 0.5,
                      }}
                      fullWidth
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                      <TextField
                        size="small"
                        label={t('delivery.length') || 'L (cm)'}
                        type="number"
                        value={item.dimensions.length}
                        onChange={(e) => handleItemChange(item.id, 'length', e.target.value)}
                        inputProps={{
                          min: 1,
                          max: currentSizeConfig.dimensions.length,
                        }}
                      />
                      <TextField
                        size="small"
                        label={t('delivery.width') || 'W (cm)'}
                        type="number"
                        value={item.dimensions.width}
                        onChange={(e) => handleItemChange(item.id, 'width', e.target.value)}
                        inputProps={{
                          min: 1,
                          max: currentSizeConfig.dimensions.width,
                        }}
                      />
                      <TextField
                        size="small"
                        label={t('delivery.height') || 'H (cm)'}
                        type="number"
                        value={item.dimensions.height}
                        onChange={(e) => handleItemChange(item.id, 'height', e.target.value)}
                        inputProps={{
                          min: 1,
                          max: currentSizeConfig.dimensions.height,
                        }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Packaging Upsell */}
          {showPackagingUpsell && (
            <Alert 
              severity="info" 
              icon={<ShoppingCart size={20} />}
              sx={{ 
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(33, 150, 243, 0.2)' : 'info.light',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                }
              }}
              onClick={() => setPackagingDialogOpen(true)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('delivery.needPackaging') || 'Need packaging materials?'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('delivery.packagingUpsell') || 'Order here and save money! We have the right packaging for your items.'}
              </Typography>
             
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button 
            onClick={handleReset}
            variant="outlined"
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            {t('common.reset') || 'Reset'}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            {t('common.apply') || 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Packaging Order Dialog */}
      <PackagingOrderDialog
        open={packagingDialogOpen}
        onClose={() => setPackagingDialogOpen(false)}
        items={items}
        suggestedQuantities={getSuggestedPackaging()}
      />
    </>
  );
};